import { HStack, Input, RecipeProps, Text, useRecipe } from '@chakra-ui/react';
import { durationInputRecipe } from '@theme/component';
import { ChangeEvent, PropsWithChildren, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

type StackDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse' | undefined;

type DurationInputVariantProps = RecipeProps<typeof durationInputRecipe>;

interface DurationInputProps extends PropsWithChildren<DurationInputVariantProps> {
  value: number;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export function DurationInput({ value, onChange }: Readonly<DurationInputProps>) {
  const recipe = useRecipe({ key: 'durationInput' });
  const styles = recipe();
  const [localDuration, setLocalDuration] = useState<{ minutes: number; seconds: number }>({
    minutes: Math.floor(value / 60),
    seconds: value % 60,
  });

  const handleDurationChange = (unit: 'min' | 'sec', e: ChangeEvent<HTMLInputElement>) => {
    let inputValue = parseInt(e.target.value, 10) || 0;

    if (unit === 'sec') {
      inputValue = Math.min(Math.max(inputValue, 0), 59);
    }

    const newDuration = {
      minutes: unit === 'min' ? inputValue : localDuration.minutes,
      seconds: unit === 'sec' ? inputValue : localDuration.seconds,
    };
    setLocalDuration(newDuration);

    const totalSeconds = newDuration.minutes * 60 + newDuration.seconds;

    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: totalSeconds.toString(),
      },
    };

    onChange(syntheticEvent);
  };

  useEffect(() => {
    setLocalDuration({
      minutes: Math.floor(value / 60),
      seconds: value % 60,
    });
  }, [value]);

  return (
    <HStack {...styles} direction={styles.direction as StackDirection}>
      <HStack width="50%" gap={1}>
        <Input
          type="number"
          variant="unstyled"
          padding={0}
          min={0}
          max={999}
          value={localDuration.minutes}
          onChange={(e) => handleDurationChange('min', e)}
        />
        <Text color="neutral.300">
          <FormattedMessage id="global.duration.min" />
        </Text>
      </HStack>
      <HStack width="50%" gap={1}>
        <Input
          type="number"
          variant="unstyled"
          min={0}
          max={59}
          padding={0}
          value={localDuration.seconds}
          onChange={(e) => handleDurationChange('sec', e)}
        />
        <Text color="neutral.300">
          <FormattedMessage id="global.duration.sec" />
        </Text>
      </HStack>
    </HStack>
  );
}
